'use client';
import { useInbox } from '@shared/providers';
import { twMerge } from '@shared/themes';
import type { ComponentProps } from 'react';

type NotificationSeverity = 'info' | 'warning' | undefined;

const NOTIFICATION_DOT_CLASS_MAP = {
  info: 'bg-notification-red',
  warning: 'bg-notification-orange',
} satisfies Record<Exclude<NotificationSeverity, undefined>, string>;

type NotificationDotProps = Omit<ComponentProps<'div'>, 'children'> & {
  notificationSeverity: NotificationSeverity;
};

/**
 * Notification dot component that displays a notification badge when there is a notification.
 * Component will check if there is a notification in the inbox and if so, it will display a notification dot.
 * If there is no notification in the inbox, it will render dot based on the notificationSeverity prop.
 * @param notificationSeverity - The severity of the notification.
 * @returns The notification dot component.
 */
function NotificationDot({ notificationSeverity, className, ...props }: NotificationDotProps) {
  const { showNotificationBadge } = useInbox();
  if (!notificationSeverity && !showNotificationBadge) return null;
  const severity = showNotificationBadge ? 'info' : notificationSeverity!;
  return <div className={twMerge('flex items-center justify-center rounded-full p-0 h-2 w-2 box-content', NOTIFICATION_DOT_CLASS_MAP[severity], className)} {...props} />;
}

export { NotificationDot };
export type { NotificationSeverity };
